import React, {Component} from "react";
import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";

import {API} from "../../../api/api";
import {ToasterService} from "../../../services/toaster/toaster.service";
import {Dialog} from "../../../utils/Dialog/Dialog.component";
import {SubsidiaryUtils} from "../../../utils/Subsidiary/SubsidiaryUtils";
import Footer from "../../home/components/Footer/footer.component";
import {
    checkAnyResourceAccess,
    isAdmin
} from "../../../utils/Authorization/authorization";
import {rtpProfilesAPI} from "../api/api";
import {
    buildRTPProfilePayload,
    EMPTY_RTP_PROFILE_DEFAULTS_FORM,
    EMPTY_RTP_PROFILE_FORM,
    profileDefaultsFormValues,
    profileFormValues,
    profileString,
    RTPProfileDefaultsFormValues,
    RTPProfileFormValues,
    validateRTPProfile
} from "../utils/rtpProfile";
import RTPProfileForm, {
    RTPProfileDefaultsForm
} from "./RTPProfileForm.component";

import "./RTPProfilesPage.component.scss";

type DialogMode = "create" | "edit" | "defaults" | null;

const isProductionEnvironment = () =>
    process.env.REACT_APP_BUILD_ENV === "PROD" ||
    !process.env.REACT_APP_BUILD_ENV;

interface RTPProfilesPageState {
    profiles: any[];
    subsidiaries: any[];
    loading: boolean;
    subsidiariesLoading: boolean;
    saving: boolean;
    dialogMode: DialogMode;
    editingProfile: any;
    form: RTPProfileFormValues;
    formIsValid: boolean;
    defaultsForm: RTPProfileDefaultsFormValues;
    defaultsFormIsValid: boolean;
}

class RTPProfilesPage extends Component<{}, RTPProfilesPageState> {
    state: RTPProfilesPageState = {
        profiles: [],
        subsidiaries: [],
        loading: true,
        subsidiariesLoading: true,
        saving: false,
        dialogMode: null,
        editingProfile: null,
        form: {...EMPTY_RTP_PROFILE_FORM},
        formIsValid: false,
        defaultsForm: {...EMPTY_RTP_PROFILE_DEFAULTS_FORM},
        defaultsFormIsValid: true
    };

    componentDidMount() {
        this.loadProfiles();
        this.loadSubsidiaries();
    }

    loadProfiles = async () => {
        this.setState({loading: true});
        try {
            const response = await rtpProfilesAPI.getProfiles();
            this.setState({
                profiles: response.data && response.data.results
                    ? response.data.results
                    : [],
                loading: false
            });
        } catch (error) {
            this.setState({loading: false});
            API.handleError(error.response, "Error getting RTP profiles.");
        }
    };

    loadSubsidiaries = async () => {
        this.setState({subsidiariesLoading: true});
        try {
            const response = await rtpProfilesAPI.getSubsidiaries();
            this.setState({
                subsidiaries: response.data && response.data.results
                    ? response.data.results
                    : [],
                subsidiariesLoading: false
            });
        } catch (error) {
            this.setState({subsidiariesLoading: false});
            API.handleError(error.response, "Error getting subsidiary list.");
        }
    };

    openCreate = () => {
        if (!isProductionEnvironment() || !isAdmin()) return;

        this.setState({
            dialogMode: "create",
            editingProfile: null,
            form: {...EMPTY_RTP_PROFILE_FORM},
            formIsValid: false
        });
    };

    openEdit = profile => {
        if (!isProductionEnvironment() || !isAdmin()) return;

        this.setState({
            dialogMode: "edit",
            editingProfile: profile,
            form: profileFormValues(profile),
            formIsValid: Object.keys(
                validateRTPProfile(profileFormValues(profile))
            ).length === 0
        });
    };

    openDefaults = profile => {
        if (
            !isProductionEnvironment() ||
            !checkAnyResourceAccess("rtp_profile", ["0b100"])
        ) return;

        this.setState({
            dialogMode: "defaults",
            editingProfile: profile,
            defaultsForm: profileDefaultsFormValues(profile),
            defaultsFormIsValid: true
        });
    };

    closeForm = () => {
        if (!this.state.saving) {
            this.setState({
                dialogMode: null,
                editingProfile: null,
                form: {...EMPTY_RTP_PROFILE_FORM},
                formIsValid: false,
                defaultsForm: {...EMPTY_RTP_PROFILE_DEFAULTS_FORM},
                defaultsFormIsValid: true
            });
        }
    };

    onFormChange = ({values, isValid}) => {
        this.setState({
            form: values,
            formIsValid: isValid
        });
    };

    onDefaultsFormChange = ({values, isValid}) => {
        this.setState({
            defaultsForm: values,
            defaultsFormIsValid: isValid
        });
    };

    saveDefaultSubsidiaries = async (profileId, previousIds, selectedIds) => {
        const added = selectedIds.filter(id => !previousIds.includes(id));
        const removed = previousIds.filter(id => !selectedIds.includes(id));

        await Promise.all([
            ...added.map(subsidiaryId =>
                rtpProfilesAPI.setDefaultProfile(profileId, subsidiaryId)
            ),
            ...removed.map(subsidiaryId =>
                rtpProfilesAPI.removeDefaultProfile(profileId, subsidiaryId)
            )
        ]);
    };

    saveProfile = async () => {
        const errors = validateRTPProfile(this.state.form);
        if (
            !isProductionEnvironment() ||
            !isAdmin() ||
            !this.state.formIsValid ||
            Object.keys(errors).length > 0 ||
            this.state.saving
        ) return;

        this.setState({saving: true});
        const payload = buildRTPProfilePayload(this.state.form);

        try {
            if (this.state.dialogMode === "create") {
                await rtpProfilesAPI.createProfile(payload);
            } else {
                await rtpProfilesAPI.editProfile(
                    this.state.editingProfile.id,
                    payload
                );
            }

            ToasterService.successToast(
                this.state.dialogMode === "create"
                    ? "RTP profile successfully created."
                    : "RTP profile successfully updated."
            );

            this.setState(
                {
                    saving: false,
                    dialogMode: null,
                    editingProfile: null,
                    form: {...EMPTY_RTP_PROFILE_FORM},
                    formIsValid: false,
                    defaultsForm: {...EMPTY_RTP_PROFILE_DEFAULTS_FORM},
                    defaultsFormIsValid: true
                },
                this.loadProfiles
            );
        } catch (error) {
            this.setState({saving: false});
            API.handleError(error.response, "Error saving RTP profile.");
        }
    };

    saveDefaults = async () => {
        if (
            !isProductionEnvironment() ||
            !checkAnyResourceAccess("rtp_profile", ["0b100"]) ||
            !this.state.editingProfile ||
            !this.state.defaultsFormIsValid ||
            this.state.saving
        ) return;

        this.setState({saving: true});

        const previousSubsidiaries = (
            this.state.editingProfile.subsidiary || []
        ).map(subsidiary => subsidiary.id);

        try {
            await this.saveDefaultSubsidiaries(
                this.state.editingProfile.id,
                previousSubsidiaries,
                this.state.defaultsForm.defaultSubsidiaries || []
            );

            ToasterService.successToast(
                "Default RTP profile subsidiaries successfully updated."
            );

            this.setState(
                {
                    saving: false,
                    dialogMode: null,
                    editingProfile: null,
                    defaultsForm: {...EMPTY_RTP_PROFILE_DEFAULTS_FORM},
                    defaultsFormIsValid: true
                },
                this.loadProfiles
            );
        } catch (error) {
            this.setState({saving: false});
            API.handleError(
                error.response,
                "Error updating default RTP profile subsidiaries."
            );
        }
    };

    renderProfiles = () => {
        if (this.state.loading) {
            return (
                <div className="rtp-profiles-loading">
                    <Components.LoadingRipple width="100px" height="100px" />
                </div>
            );
        }

        if (this.state.profiles.length === 0) {
            return <div className="rtp-profiles-empty">No RTP profiles found.</div>;
        }

        const actionsAvailable = isProductionEnvironment();
        const canSetDefaults =
            actionsAvailable &&
            checkAnyResourceAccess("rtp_profile", ["0b100"]);

        return (
            <div className="rtp-profiles-list">
                {this.state.profiles.map(profile => {
                    const subsidiaries = profile.subsidiary || [];

                    return (
                        <div className="rtp-profile-card" key={profile.id}>
                            <div className="rtp-profile-card-body">
                                <label>Profile</label>
                                <h2>{profileString(profile)}</h2>
                                <label>Is Default Of</label>
                                <div className="default-subsidiaries">
                                    {subsidiaries.length > 0
                                        ? subsidiaries.map(subsidiary => (
                                              <span
                                                  className="subsidiary-icon tooltip-icon"
                                                  key={subsidiary.id}
                                              >
                                                  <span className="tooltip-text">
                                                      {subsidiary.description ||
                                                          subsidiary.name}
                                                  </span>
                                                  {SubsidiaryUtils.subsidiaryIcon(
                                                      subsidiary
                                                  )}
                                              </span>
                                          ))
                                        : "-"}
                                </div>
                            </div>

                            <div className="rtp-profile-card-footer">
                                {actionsAvailable && isAdmin() && (
                                    <button
                                        aria-label={`Edit ${profileString(profile)}`}
                                        className="tooltip-icon"
                                        onClick={() => this.openEdit(profile)}
                                    >
                                        <span className="tooltip-text">Edit</span>
                                        <i className="icon-pencil-alt-light" />
                                    </button>
                                )}
                                {canSetDefaults && (
                                    <button
                                        aria-label={`Set default subsidiaries for ${profileString(profile)}`}
                                        className="tooltip-icon"
                                        disabled={this.state.subsidiariesLoading}
                                        onClick={() => this.openDefaults(profile)}
                                    >
                                        <span className="tooltip-text">
                                            Set default subsidiaries
                                        </span>
                                        <i className="icon-tag-light" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    render() {
        const formOpen = this.state.dialogMode !== null;
        const actionsAvailable = isProductionEnvironment();

        return (
            <div id="RTPProfilesPage">
                <div className="content">
                    <div className="container">
                        <div className="rtp-profiles-header">
                            <h2>RTP Profiles</h2>
                            {actionsAvailable && isAdmin() && (
                                <button
                                    className="red-btn"
                                    onClick={this.openCreate}
                                >
                                    New RTP Profile
                                </button>
                            )}
                        </div>
                        {this.renderProfiles()}
                    </div>
                </div>

                <Dialog
                    open={formOpen}
                    onClose={this.closeForm}
                    onConfirm={
                        this.state.dialogMode === "defaults"
                            ? this.saveDefaults
                            : this.saveProfile
                    }
                    confirmLabel={
                        this.state.dialogMode === "create" ? "Create" : "Save"
                    }
                    validSubmit={
                        (this.state.dialogMode === "defaults"
                            ? this.state.defaultsFormIsValid
                            : this.state.formIsValid) && !this.state.saving
                    }
                    addedClassName="rtp-profile-form-dialog"
                >
                    {formOpen && (
                        <div className="rtp-profile-form">
                            <h3>
                                {this.state.dialogMode === "create"
                                    ? "New RTP Profile"
                                    : this.state.dialogMode === "edit"
                                    ? "Edit RTP Profile"
                                    : `Set Default Subsidiaries for ${profileString(
                                          this.state.editingProfile
                                      )}`}
                            </h3>
                            {this.state.dialogMode === "defaults" ? (
                                <RTPProfileDefaultsForm
                                    key={`defaults-${
                                        this.state.editingProfile.id
                                    }-${this.state.subsidiaries.length}`}
                                    initialValues={this.state.defaultsForm}
                                    subsidiaries={this.state.subsidiaries}
                                    onChange={this.onDefaultsFormChange}
                                />
                            ) : (
                                <React.Fragment>
                                    <RTPProfileForm
                                        key={`${this.state.dialogMode}-${
                                            this.state.editingProfile
                                                ? this.state.editingProfile.id
                                                : "new"
                                        }`}
                                        initialValues={this.state.form}
                                        onChange={this.onFormChange}
                                    />
                                    <div className="profile-preview">
                                        <span>Profile</span>
                                        <strong>
                                            {this.state.formIsValid
                                                ? profileString(
                                                      buildRTPProfilePayload(
                                                          this.state.form
                                                      )
                                                  )
                                                : "Complete all fields to preview the profile"}
                                        </strong>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </Dialog>

                <Footer />
            </div>
        );
    }
}

export default RTPProfilesPage;
